import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSharedLinks, deleteSharedLink, type SharedLink } from '@/utils/sharedLinksDB';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, MoreVertical, ExternalLink, Copy, Trash, Edit, Eye, EyeOff } from 'lucide-react';

const Links: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<SharedLink | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: links = [], isLoading, error } = useQuery({
    queryKey: ['shared-links'],
    queryFn: getSharedLinks,
  });

  const handleCopyLink = (shortId: string) => {
    const url = `https://pricingsaas.com/share/${shortId}`;
    navigator.clipboard.writeText(url);
    toast({
      title: 'Link copied',
      description: 'Share link has been copied to clipboard',
    });
  };

  const handleDeleteClick = (link: SharedLink) => {
    setLinkToDelete(link);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;

    setDeletingId(linkToDelete.id);
    setDeleteConfirmOpen(false);

    try {
      await deleteSharedLink(linkToDelete.id);
      queryClient.invalidateQueries({ queryKey: ['shared-links'] });
      toast({
        title: 'Link deleted',
        description: 'Shared link has been deleted successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to delete link',
        description: error.message || 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
      setLinkToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Loading shared links...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-red-800">Failed to load shared links</p>
            <p className="text-sm text-red-600 mt-2">{(error as Error).message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Shared Links</h1>
        <p className="text-gray-500">
          Manage shareable comparison links for before-and-after pricing screenshots
        </p>
      </div>

      {links.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-gray-600">No shared links yet.</p>
            <p className="text-sm text-gray-500 mt-2">
              Create a shared link from event annotations in the Diffs page.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {links.length} {links.length === 1 ? 'Link' : 'Links'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[180px]">Created</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {link.is_active ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-300">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-600">
                            Inactive
                          </Badge>
                        )}
                        {link.require_login ? (
                          <Badge variant="outline" className="text-xs">
                            <Eye className="h-3 w-3 mr-1" />
                            Login
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            <EyeOff className="h-3 w-3 mr-1" />
                            Public
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(link.created_at)}
                    </TableCell>
                    <TableCell>
                      {link.slug ? (
                        <span className="text-sm text-gray-700">{link.slug}</span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {link.title || 'Untitled'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLink(link.short_id)}
                          title="Copy link"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`https://pricingsaas.com/share/${link.short_id}`, '_blank')}
                          title="Open link"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={deletingId === link.id}
                            >
                              {deletingId === link.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <MoreVertical className="h-4 w-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(link)}
                              className="text-red-600"
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shared Link?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this shared link? This action cannot be undone.
              {linkToDelete && (
                <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {linkToDelete.title || 'Untitled'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    /share/{linkToDelete.short_id}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Links;
